import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  Collapse,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Checkbox,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomSnackbar from "../components/CustomSnackbar";
import useTradeDelay from "./TradeDelay/hooks/useTradeDelay";
import BasketsTab from "./TradeDelay/components/BasketsTab";
import ManagersAgentsTab from "./TradeDelay/components/ManagersAgentsTab";
import RejectOrderDialog from "./TradeDelay/components/orderRow/RejectOrderDialog";
import OrderSummaryRow from "./TradeDelay/components/orderRow/OrderSummaryRow";
import OrderItemsTableHead from "./TradeDelay/components/orderRow/OrderItemsTableHead";
import ItemEditCells from "./TradeDelay/components/orderRow/ItemEditCells";
import OrderActionsBar from "./TradeDelay/components/orderRow/OrderActionsBar";
import useOrderItemEdits from "./TradeDelay/components/orderRow/useOrderItemEdits";
import useOrderRelease from "./TradeDelay/components/orderRow/useOrderRelease";
import { formatDate, formatNum } from "./TradeDelay/utils/formatters";
import TradeDelayHeader from "./TradeDelay/components/page/TradeDelayHeader";
import TradeDelayTabs, { TABS } from "./TradeDelay/components/page/TradeDelayTabs";
import BranchContextBanner from "./TradeDelay/components/page/BranchContextBanner";
import OrdersFilterBar from "./TradeDelay/components/page/OrdersFilterBar";
import { OrdersEmptyMessage, OrdersLoading, noMatchesMessage } from "./TradeDelay/components/page/OrdersStatusMessages";
import { AutoReleaseDialogTitle, SelectAllCheckbox, SkippedOrdersList } from "./TradeDelay/components/page/AutoReleaseDialogParts";
import ReleaseResultsDialog from "./TradeDelay/components/page/ReleaseResultsDialog";
import useOrdersFilter from "./TradeDelay/hooks/useOrdersFilter";
import useAutoReleaseSelection from "./TradeDelay/hooks/useAutoReleaseSelection";
import useBranchContext from "./TradeDelay/hooks/useBranchContext";
import useRejectOrder from "./TradeDelay/hooks/useRejectOrder";
import { useUser } from "../contexts/UserContext";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";

const DISCOUNT_FIELDS = [
  { key: "NEO_CONSTDISC", label: "הנחה קבועה" },
  { key: "NEO_AGENTDISC", label: "הנחת סוכן" },
  { key: "NEO_SALEDISC", label: "הנחת מבצע" },
  { key: "NEO_NEWPARTDISC", label: "הנחת מוצר חדש" },
  { key: "NEO_MARKDISC", label: "הנחת יעדים" },
  { key: "NEO_ORIGDISC", label: "הנחה מקורית" },
  { key: "NEO_EDIPERCENT", label: "הנחת ממשק" },
];

const MAIN_COLS = 8;

// ─── Item Row with expandable discounts ──────────────────────
// extraCells: optional React fragment of <TableCell> elements to replace the PRICE cell
const ItemRow = ({ item, extraCells }) => {
  const [open, setOpen] = useState(false);
  const colSpan = extraCells ? 10 : 6;
  const hasAgentException = !!item.NEO_CHECKAGENTDISC;

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: "pointer",
          bgcolor: hasAgentException
            ? open
              ? "#fee2e2"
              : "#fef2f2" // ← אדום אם חריגת סוכן
            : open
              ? "#f0f7ff"
              : "transparent",
          "& > *": {
            borderBottom: open ? "none" : "1px solid rgba(0,0,0,0.05)",
          },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <TableCell sx={{ width: 36, pr: 0 }}>
          <IconButton
            size="small"
            sx={{ color: hasAgentException ? "#dc2626" : "#0054A5" }}
          >
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>

        {/* מק"ט — אדום + tooltip אם יש חריגה */}
        <TableCell
          sx={{
            fontWeight: 600,
            color: hasAgentException ? "#dc2626" : "#0054A5",
          }}
        >
          {hasAgentException ? (
            <Tooltip title="קיימת חריגת סוכן">
              <span>{item.PARTNAME}</span>
            </Tooltip>
          ) : (
            item.PARTNAME
          )}
        </TableCell>

        <TableCell
          title={item.PDES}
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.PDES}
        </TableCell>
        <TableCell align="center">{item.BARCODE}</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>
          {item.TQUANT}
        </TableCell>
        {extraCells ? (
          extraCells
        ) : (
          <TableCell align="center">{formatNum(item.PRICE)}</TableCell>
        )}
      </TableRow>

      {/* Discounts expanded row */}
      <TableRow>
        <TableCell colSpan={colSpan} sx={{ p: 0, border: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                bgcolor: hasAgentException ? "#fff5f5" : "#f8fafc",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                overflowX: "auto",
              }}
            >
              {hasAgentException && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 1,
                    color: "#dc2626",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                >
                  קיימת חריגת סוכן בפריט זה
                </Typography>
              )}
              <Typography
                variant="caption"
                fontWeight={700}
                color="#6b7280"
                sx={{
                  display: "block",
                  mb: 0.5,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                הנחות מוצר
              </Typography>
              <Table
                size="small"
                sx={{
                  "& td, & th": {
                    px: 1.5,
                    py: 0.6,
                    fontSize: "0.75rem",
                    border: "none",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {DISCOUNT_FIELDS.map((d) => (
                      <TableCell
                        key={d.key}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          color:
                            d.key === "NEO_AGENTDISC" && hasAgentException
                              ? "#dc2626"
                              : "#6b7280",
                        }}
                      >
                        {d.label} %
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    sx={{ bgcolor: hasAgentException ? "#fee2e2" : "#f0f7ff" }}
                  >
                    {DISCOUNT_FIELDS.map((d) => (
                      <TableCell
                        key={d.key}
                        align="center"
                        sx={{
                          fontWeight: item[d.key] > 0 ? 700 : 400,
                          color:
                            d.key === "NEO_AGENTDISC" && hasAgentException
                              ? "#dc2626"
                              : item[d.key] > 0
                                ? "#0054A5"
                                : "#9ca3af",
                        }}
                      >
                        {item[d.key] > 0 ? `${formatNum(item[d.key])}%` : "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const DiscountChip = ({ label, value, highlight, active, color }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      bgcolor: highlight
        ? color === "#217346"
          ? "#f0faf4"
          : "#ebf7ff"
        : active
          ? "#f5f5f5"
          : "#fafafa",
      border: `1px solid ${highlight ? (color === "#217346" ? "#c6ecd4" : "#c5def7") : active ? "#e0e0e0" : "#f0f0f0"}`,
      borderRadius: 2,
      px: 1.5,
      py: 0.5,
      minWidth: 80,
    }}
  >
    <Typography variant="caption" color="#6b7280" sx={{ fontSize: "0.65rem" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={highlight || active ? 700 : 400}
      color={color || (active ? "#1f2937" : "#9ca3af")}
      sx={{ fontSize: "0.8rem" }}
    >
      {value}
    </Typography>
  </Box>
);

const LastSalesDialog = ({
  open,
  onClose,
  custName,
  partName,
  fetchLastSales,
}) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);

  // טעינה ראשונית כשהדיאלוג נפתח
  useEffect(() => {
    if (!open) return;
    setSales([]);
    setSkip(0);
    setHasMore(true);
    loadSales(0, true);
  }, [open, custName, partName]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSales = async (currentSkip, isFirst = false) => {
    isFirst ? setLoading(true) : setLoadingMore(true);
    try {
      const results = await fetchLastSales(custName, partName, currentSkip);
      setSales((prev) => (isFirst ? results : [...prev, ...results]));
      setHasMore(results.length === 10);
      setSkip(currentSkip + results.length);
    } catch {
      setHasMore(false);
    } finally {
      isFirst ? setLoading(false) : setLoadingMore(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir="rtl"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ bgcolor: "#ebf7ff", px: 3, pt: 3, pb: 1.5 }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <HistoryIcon sx={{ color: "#0054A5" }} />
              <Box>
                <Typography variant="h6" fontWeight="bold" color="#1f2937">
                  הזמנות אחרונות
                </Typography>
                <Typography variant="caption" color="#6b7280">
                  מק"ט: {partName} | לקוח: {custName}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "#6b7280" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : sales.length === 0 ? (
          <Typography color="#6b7280" textAlign="center" py={3}>
            לא נמצאו הזמנות קודמות למוצר זה
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#ebf7ff" }}>
                    {[
                      "מס' הזמנה",
                      "תאריך",
                      "סטטוס",
                      "כמות",
                      "מחיר ליחידה",
                      "מחיר אחרי הנחה",
                      "הנחה %",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.78rem",
                          color: "#1f2937",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sales.map((s, i) => (
                    <TableRow
                      key={i}
                      hover
                      sx={{ "& > *": { fontSize: "0.82rem" } }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#0054A5" }}>
                        {s.ORDNAME}
                      </TableCell>
                      <TableCell>{formatDate(s.CURDATE)}</TableCell>
                      <TableCell>
                        <Chip
                          label={s.ORDSTATUSDES}
                          size="small"
                          sx={{ fontSize: "0.68rem", height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        {s.TQUANT}
                      </TableCell>
                      <TableCell align="center">{formatNum(s.PRICE)}</TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 600, color: "#217346" }}
                      >
                        {formatNum(s.DISPRICE)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: "#0054A5" }}>
                        {formatNum(s.PERCENT)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Load More */}
            {hasMore && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => loadSales(skip)}
                  disabled={loadingMore}
                  startIcon={
                    loadingMore ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                  sx={{
                    borderColor: "#0054A5",
                    color: "#0054A5",
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#ebf7ff" },
                  }}
                >
                  {loadingMore ? "טוען..." : `טען עוד 10`}
                </Button>
              </Box>
            )}

            {!hasMore && sales.length > 0 && (
              <Typography
                variant="caption"
                color="#9ca3af"
                display="block"
                textAlign="center"
                mt={1.5}
              >
                הוצגו כל {sales.length} ההזמנות
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
const OrderItemsTable = ({ items, itemEdits, onLastSales }) => (
  <Box bgcolor="#ffffff" borderRadius={2} border="1px solid rgba(0,0,0,0.08)" mb={2.5}>
    <TableContainer sx={{ maxHeight: 400, borderRadius: 2 }}>
      <Table size="small" stickyHeader>
        <OrderItemsTableHead />
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 3, color: "#6b7280" }}>
                אין פריטים
              </TableCell>
            </TableRow>
          )}
          {items.map((item, idx) => {
            const k = String(idx);
            const extraCells = (
              <ItemEditCells
                item={item}
                edit={itemEdits.edits[k] || {}}
                isEditing={itemEdits.editingKeys.has(k)}
                isSaved={itemEdits.savedKeys.has(k)}
                onChange={(field, value) => itemEdits.changeField(k, field, value)}
                onToggleEdit={(e) => itemEdits.toggleEdit(k, e)}
                onLastSales={() => onLastSales(item.PARTNAME)}
              />
            );
            return <ItemRow key={idx} item={item} extraCells={extraCells} />;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const OrderDetails = ({ open, items, itemEdits, release, onLastSales, onRejectClick }) => (
  <TableRow>
    <TableCell colSpan={MAIN_COLS} sx={{ p: 0, border: "none" }}>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ bgcolor: "#f8fafc", borderBottom: "2px solid #ebf7ff", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="#1f2937" mb={1}>
            פריטי הזמנה ({items.length})
          </Typography>
          <OrderItemsTable items={items} itemEdits={itemEdits} onLastSales={onLastSales} />
          {itemEdits.saveError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
              {itemEdits.saveError}
            </Typography>
          )}
          <Divider sx={{ mb: 2 }} />
          <OrderActionsBar itemEdits={itemEdits} release={release} onRejectClick={onRejectClick} />
        </Box>
      </Collapse>
    </TableCell>
  </TableRow>
);

// One order in the manual-release table: summary line + expandable details.
const OrderRow = ({ order, releaseStatuses, onUpdateStatus, onReject, updateOrderItem, fetchLastSales, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [salesDialog, setSalesDialog] = useState({ open: false, custName: "", partName: "" });
  const items = order?.ORDERITEMS_SUBFORM || [];
  const itemEdits = useOrderItemEdits({ order, items, updateOrderItem });
  const release = useOrderRelease({ order, releaseStatuses, onUpdateStatus, onReleased: () => setOpen(false) });

  return (
    <>
      <OrderSummaryRow order={order} open={open} onToggle={() => setOpen((v) => !v)} />
      <OrderDetails
        open={open}
        items={items}
        itemEdits={itemEdits}
        release={release}
        onLastSales={(partName) => setSalesDialog({ open: true, custName: order.CUSTNAME, partName })}
        onRejectClick={onReject ? () => setRejectOpen(true) : null}
      />
      {onReject && (
        <RejectOrderDialog open={rejectOpen} ordname={order.ORDNAME} onClose={() => setRejectOpen(false)} onReject={onReject} />
      )}
      <LastSalesDialog
        open={salesDialog.open}
        onClose={() => setSalesDialog((prev) => ({ ...prev, open: false }))}
        custName={salesDialog.custName}
        partName={salesDialog.partName}
        fetchLastSales={fetchLastSales}
      />
    </>
  );
};

// ─── Preview Order Row (auto-release dialog, with inline item editing) ────────
const PreviewOrderRow = ({
  order,
  checked,
  onCheck,
  onDirtyChange,
  updateOrderItem,
}) => {
  const [open, setOpen] = useState(false);
  const [editingKeys, setEditingKeys] = useState(new Set()); // string indices
  const [edits, setEdits] = useState({});
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const items = order.items || [];

  // Dirty = edit mode open with unsaved changes
  const hasDirty = [...editingKeys].some(
    (k) => !savedKeys.has(k) && edits[k] && Object.keys(edits[k]).length > 0,
  );

  // Inform parent whenever dirty state changes
  useEffect(() => {
    onDirtyChange(order.ordname, hasDirty);
  }, [hasDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleEdit = (k, e) => {
    e.stopPropagation();
    setEditingKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
        if (!savedKeys.has(k)) {
          setEdits((prev2) => {
            const ne = { ...prev2 };
            delete ne[k];
            return ne;
          });
        }
      } else {
        next.add(k);
      }
      return next;
    });
    setSaveError(null);
  };

  const handleFieldChange = (k, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [k]: { ...(prev[k] || {}), [field]: value },
    }));
    setSavedKeys((prev) => {
      const s = new Set(prev);
      s.delete(k);
      return s;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      for (const k of editingKeys) {
        if (savedKeys.has(k)) continue;
        const edit = edits[k] || {};
        const updates = {};
        if (edit.NEO_AGENTDISC !== undefined && edit.NEO_AGENTDISC !== "")
          updates.NEO_AGENTDISC = Number(edit.NEO_AGENTDISC);
        if (Object.keys(updates).length > 0 && updateOrderItem) {
          const item = items[Number(k)];
          const kline = item?.kline ?? item?.KLINE;
          if (kline != null) {
            await updateOrderItem(order.ordname, kline, updates);
          }
        }
        setSavedKeys((prev) => new Set([...prev, k]));
      }
      setEditingKeys(new Set());
      // Keep edits as display overrides so the saved values remain visible
    } catch (err) {
      setSaveError(err?.message || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ListItem disablePadding sx={{ py: 0.3 }}>
        <Tooltip title={hasDirty ? "שמור שינויים לפני הסימון" : ""}>
          <span>
            <Checkbox
              size="small"
              checked={checked}
              disabled={hasDirty}
              onChange={(e) => onCheck(order.ordname, e.target.checked)}
            />
          </span>
        </Tooltip>
        <Box
          flexGrow={1}
          display="flex"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
        >
          <strong>{order.ordname}</strong>
          <Typography variant="caption" color="#6b7280">
            ({order.custName})
          </Typography>
          <Chip
            label={order.newStatus}
            size="small"
            sx={{
              bgcolor: "#ebf7ff",
              color: "#0054A5",
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 20,
            }}
          />
          <Typography variant="caption" color="#9ca3af">
            סל: {order.basket}
          </Typography>
          {hasDirty && (
            <Chip
              label="יש שינויים לא שמורים"
              size="small"
              sx={{
                bgcolor: "#fff3e0",
                color: "#e65100",
                fontSize: "0.65rem",
                height: 18,
              }}
            />
          )}
        </Box>
        {items.length > 0 && (
          <Tooltip title={open ? "סגור פריטים" : "ערוך פריטים"}>
            <IconButton
              size="small"
              onClick={() => setOpen((v) => !v)}
              sx={{ color: "#0054A5" }}
            >
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </ListItem>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 4, pr: 1, pb: 1.5 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                  {[
                    "מקט",
                    "תיאור",
                    "כמות",
                    "מחיר ליחידה",
                    "הנחת סוכן %",
                    "",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        py: 0.7,
                        color: "#374151",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => {
                  const k = String(idx);
                  const isEditing = editingKeys.has(k);
                  const isSaved = savedKeys.has(k);
                  const edit = edits[k] || {};

                  return (
                    <TableRow
                      key={k}
                      sx={{
                        bgcolor: isSaved
                          ? "#f0faf4"
                          : isEditing
                            ? "#f0f7ff"
                            : "transparent",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#0054A5",
                        }}
                      >
                        {item.partName}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.75rem",
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.pdes}
                      >
                        {item.pdes}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.75rem" }}>
                        {item.tquant}
                      </TableCell>

                      {/* PRICE — read-only */}
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.78rem", color: "#1f2937" }}
                        >
                          {formatNum(item.price)}
                        </Typography>
                      </TableCell>

                      {/* NEO_AGENTDISC — read-only text or TextField */}
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            value={
                              edit.NEO_AGENTDISC ?? item.neoAgentDisc ?? ""
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                k,
                                "NEO_AGENTDISC",
                                e.target.value,
                              )
                            }
                            inputProps={{
                              style: {
                                fontSize: "0.78rem",
                                padding: "3px 6px",
                                width: 60,
                              },
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": { borderRadius: 1 },
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.78rem",
                              color: isSaved ? "#217346" : "#1f2937",
                              fontWeight: isSaved ? 700 : 400,
                            }}
                          >
                            {formatNum(edit.NEO_AGENTDISC ?? item.neoAgentDisc)}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Pencil / close toggle */}
                      <TableCell align="center">
                        <Tooltip
                          title={isEditing ? "בטל עריכה" : "ערוך מחיר / הנחה"}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => toggleEdit(k, e)}
                            sx={{
                              color: isEditing ? "#dc2626" : "#6b7280",
                              "&:hover": {
                                color: isEditing ? "#b91c1c" : "#0054A5",
                              },
                            }}
                          >
                            {isEditing ? (
                              <CloseIcon fontSize="small" />
                            ) : (
                              <EditIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {saveError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              {saveError}
            </Typography>
          )}

          {hasDirty && (
            <Box mt={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  saving ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={saving}
                onClick={handleSave}
                sx={{
                  bgcolor: "#0054A5",
                  borderRadius: 2,
                  fontSize: "0.78rem",
                  "&:hover": { bgcolor: "#003d7a" },
                }}
              >
                {saving ? "שומר..." : "שמור עדכונים ב-Priority"}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </>
  );
};

const ORDER_COLUMNS = ["מס' הזמנה", "לקוח", "תאריך הזמנה", "תאריך אספקה", "סוכן", "חריגת אובליגו", "סטטוס"];

const OrdersTable = ({ orders, linkedOrdname, rowProps }) => (
  <Fade in timeout={800}>
    <Box bgcolor="#fafafa" borderRadius={2} p={2}>
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#ebf7ff" }}>
              <TableCell sx={{ width: 48 }} />
              {ORDER_COLUMNS.map((col) => (
                <TableCell key={col} sx={{ fontWeight: "bold", color: "#1f2937", py: 1.5 }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <OrderRow key={order.ORDNAME} order={order} defaultOpen={order.ORDNAME === linkedOrdname} {...rowProps} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </Fade>
);

// Orders tab: branch banner, filters, and the manual-release table.
const OrdersTab = ({ tradeDelay, filter, linkedOrdname, branchBanner, rowProps }) => {
  const { orders, loading } = tradeDelay;
  const { filteredOrders } = filter;
  return (
    <>
      {branchBanner}
      {!loading && orders.length > 0 && <OrdersFilterBar filter={filter} />}
      {loading && <OrdersLoading />}
      {!loading && orders.length === 0 && <OrdersEmptyMessage message="לא נמצאו הזמנות בסטטוס השהיית סחר" />}
      {!loading && orders.length > 0 && filteredOrders.length === 0 && (
        <OrdersEmptyMessage message={noMatchesMessage(linkedOrdname, filter.values.searchText)} />
      )}
      {!loading && filteredOrders.length > 0 && (
        <OrdersTable orders={filteredOrders} linkedOrdname={linkedOrdname} rowProps={rowProps} />
      )}
    </>
  );
};

const PreviewOrdersList = ({ toRelease, selection, updateOrderItem }) => (
  <Box>
    <Typography variant="body2" color="#6b7280" mb={2}>
      נמצאו {toRelease.length} הזמנות העומדות בתנאי השחרור. ניתן לערוך מחיר / הנחת סוכן לפני אישור. בחר את
      ההזמנות לשחרור:
    </Typography>
    <SelectAllCheckbox selection={selection} />
    <Box bgcolor="#f8fafc" borderRadius={2} border="1px solid rgba(0,0,0,0.08)" px={1} py={0.5}>
      <List dense disablePadding>
        {toRelease.map((r) => (
          <PreviewOrderRow
            key={r.ordname}
            order={r}
            checked={selection.selectedOrdnames.includes(r.ordname)}
            onCheck={selection.toggleOrder}
            onDirtyChange={selection.handleDirtyChange}
            updateOrderItem={updateOrderItem}
          />
        ))}
      </List>
    </Box>
  </Box>
);

const ConfirmReleaseButton = ({ selection, releasing, onConfirm }) => (
  <Button
    variant="contained"
    disabled={selection.selectedOrdnames.length === 0 || releasing || selection.hasDirty}
    startIcon={releasing ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
    onClick={() => onConfirm(selection.selectedOrders)}
    sx={{ bgcolor: "#217346", borderRadius: 2, px: 3, "&:hover": { bgcolor: "#1a5c38" } }}
  >
    {releasing ? "משחרר..." : `שחרר ${selection.selectedOrdnames.length} הזמנות`}
  </Button>
);

// Auto-release preview: pick which matching orders to release.
const AutoReleasePreviewDialog = ({ previewData, selection, releasing, onConfirm, updateOrderItem }) => {
  const toRelease = previewData.toRelease || [];
  const skipped = previewData.skipped || [];
  return (
    <Dialog open onClose={selection.close} maxWidth="md" fullWidth dir="rtl" PaperProps={{ sx: { borderRadius: 3 } }}>
      <AutoReleaseDialogTitle title="שחרור אוטומטי" />
      <DialogContent sx={{ p: 3 }}>
        {toRelease.length > 0 ? (
          <PreviewOrdersList toRelease={toRelease} selection={selection} updateOrderItem={updateOrderItem} />
        ) : (
          <Typography color="#6b7280" textAlign="center" py={2}>
            אין הזמנות לשחרור אוטומטי
          </Typography>
        )}
        {skipped.length > 0 && <SkippedOrdersList skipped={skipped} hasToRelease={toRelease.length > 0} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={selection.close} sx={{ borderRadius: 2, color: "#6b7280" }}>
          ביטול
        </Button>
        {toRelease.length > 0 && <ConfirmReleaseButton selection={selection} releasing={releasing} onConfirm={onConfirm} />}
      </DialogActions>
    </Dialog>
  );
};

// Snackbar + auto-release preview / results dialogs.
const TradeDelayDialogs = ({ tradeDelay, selection }) => (
  <>
    <CustomSnackbar
      open={tradeDelay.snackbar.open}
      message={tradeDelay.snackbar.message}
      severity={tradeDelay.snackbar.severity}
      onClose={() => tradeDelay.setSnackbar((s) => ({ ...s, open: false }))}
    />
    {tradeDelay.previewData && (
      <AutoReleasePreviewDialog
        previewData={tradeDelay.previewData}
        selection={selection}
        releasing={tradeDelay.releasing}
        onConfirm={tradeDelay.confirmRelease}
        updateOrderItem={tradeDelay.updateOrderItem}
      />
    )}
    {tradeDelay.releaseResult && (
      <ReleaseResultsDialog result={tradeDelay.releaseResult} onClose={() => tradeDelay.setReleaseResult(null)} />
    )}
  </>
);

const tabContentSx = {
  borderRadius: 0,
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  border: "1px solid rgba(0,0,0,0.08)",
  borderTop: "1px solid rgba(0,0,0,0.06)",
  p: { xs: 2, md: 3 },
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
};

// External suppliers get a read-only orders list only — no baskets, managers
// tab, rejection, or auto-release.
const useTradeDelayAccess = () => {
  const { user } = useUser();
  const isAdmin = user?.roles?.some((r) => r.admin);
  return {
    isSupplier: user?.tradeDelayRole === "supplier",
    isEmployee: user?.tradeDelayRole === "employee",
    hasTradeDelayRole: isAdmin || !!user?.tradeDelayRole,
  };
};

const TradeDelayPage = () => {
  const { execute } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
  const tradeDelay = useTradeDelay(execute);
  const rejectOrder = useRejectOrder(execute, tradeDelay.setSnackbar);
  const access = useTradeDelayAccess();
  const branchContext = useBranchContext(execute, access.hasTradeDelayRole);
  const [tab, setTab] = useState(TABS.ORDERS);
  // Direct link from a notification email: /TradeDelay/:ordname filters to that order and opens it.
  const { ordname: linkedOrdname } = useParams();
  const filter = useOrdersFilter(tradeDelay.orders, linkedOrdname || "");
  const selection = useAutoReleaseSelection(tradeDelay);
  const rowProps = {
    releaseStatuses: tradeDelay.releaseStatuses,
    onUpdateStatus: tradeDelay.updateOrderStatus,
    onReject: access.isSupplier ? null : rejectOrder,
    updateOrderItem: tradeDelay.updateOrderItem,
    fetchLastSales: tradeDelay.fetchLastSales,
  };

  return (
    <div className="App">
      <Box p={{ xs: 2, md: 3 }} sx={{ maxWidth: "100%", overflowX: "hidden" }}>
        <TradeDelayHeader
          showActions={tab === TABS.ORDERS}
          counts={{ total: tradeDelay.orders.length, shown: filter.filteredOrders.length, filtered: filter.hasActiveFilters }}
          loading={tradeDelay.loading}
          isSupplier={access.isSupplier}
          previewing={tradeDelay.previewing}
          onPreview={tradeDelay.runPreview}
          onRefresh={tradeDelay.refreshOrders}
        />
        <TradeDelayTabs tab={tab} onChange={setTab} isSupplier={access.isSupplier} />
        <Paper elevation={0} sx={tabContentSx}>
          {tab === TABS.ORDERS && (
            <OrdersTab
              tradeDelay={tradeDelay}
              filter={filter}
              linkedOrdname={linkedOrdname}
              branchBanner={<BranchContextBanner branchContext={branchContext} isEmployee={access.isEmployee} />}
              rowProps={rowProps}
            />
          )}
          {tab === TABS.BASKETS && <BasketsTab />}
          {tab === TABS.MANAGERS && !access.isSupplier && <ManagersAgentsTab execute={execute} />}
        </Paper>
        <TradeDelayDialogs tradeDelay={tradeDelay} selection={selection} />
      </Box>
    </div>
  );
};

export default TradeDelayPage;
